import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <SchemaOrg
        data={{
          '@type': 'AboutPage',
          name: 'About the Hipobuy Spreadsheet Project',
          url: 'https://hipobuyspread.casa/about',
          description: 'Learn about the Hipobuy Spreadsheet project — our mission, research methodology, editorial policy, and commitment to providing organized, trustworthy product discovery resources.',
          about: {
            '@type': 'Organization',
            name: 'Hipobuy Spreadsheet',
          },
        }}
      />
      <SchemaOrg
        data={{
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://hipobuyspread.casa/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'About This Project',
              item: 'https://hipobuyspread.casa/about',
            },
          ],
        }}
      />
      <Navbar />

      <main className="pt-20 pb-16">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            <nav className="mb-8" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm font-body text-foreground-500">
                <li><Link to="/" className="hover:text-primary-500 transition-colors">Home</Link></li>
                <li><span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span></li>
                <li><span className="text-foreground-700">About This Project</span></li>
              </ol>
            </nav>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-6">
              About the Hipobuy Spreadsheet Project
            </h1>

            <div className="space-y-6 text-sm md:text-base font-body text-foreground-700 leading-relaxed">
              <section>
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-3">Our Mission</h2>
                <p>
                  The Hipobuy Spreadsheet project was created with a single, focused mission: to build the most organized, trustworthy, and comprehensive product discovery resource available online. In a digital world where shopping information is fragmented across countless platforms, we centralize curated product data into structured spreadsheets that save shoppers time, reduce decision fatigue, and enable smarter purchasing decisions.
                </p>
              </section>

              <section>
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-3">Why This Website Exists</h2>
                <p>
                  Shopping online should not feel like solving a puzzle. Yet millions of people spend hours every week jumping between websites, comparing listings, and trying to determine which products are genuinely worth their attention. The Hipobuy Spreadsheet addresses this inefficiency head-on — by organizing product references into consistently formatted, categorically grouped databases that anyone can browse and benefit from.
                </p>
                <p className="mt-3">
                  We noticed that the most effective shoppers — collectors, fashion enthusiasts, and savvy consumers — all used some form of personal spreadsheet to track their discoveries. The Hipobuy Spreadsheet project takes that proven methodology and scales it into a public resource that benefits the entire shopping community.
                </p>
              </section>

              <section>
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-3">Research Methodology</h2>
                <p>
                  Every category spreadsheet is built through a combination of community-sourced product discoveries and systematic market research. Products are verified for listing accuracy before being added to any category. Categories are regularly reviewed to ensure they reflect current market conditions and community shopping patterns.
                </p>
                <p className="mt-3">
                  Our update cadence varies by category: core apparel categories like Shoes and Hoodies receive weekly updates, while accessories and lifestyle categories are refreshed monthly. This structured approach ensures that high-demand categories stay current while less volatile categories maintain consistent quality.
                </p>
              </section>

              <section>
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-3">Content Update Process</h2>
                <p>
                  Content freshness is a core commitment. Each spreadsheet category displays its last-updated date. Major updates — new category additions, format improvements, and database expansions — are logged and visible. We maintain an internal review cycle that checks every category at least once per month for accuracy and completeness.
                </p>
              </section>

              <section className="bg-background-100 rounded-lg p-6 mt-8">
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-3">Editorial Policy</h2>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0"><i className="ri-check-line text-accent-500"></i></span>
                    <span>All product information is verified before inclusion in any category spreadsheet.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0"><i className="ri-check-line text-accent-500"></i></span>
                    <span>External links are clearly marked and open in new tabs for transparency.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0"><i className="ri-check-line text-accent-500"></i></span>
                    <span>No payment collection, no registration requirements, no personal data tracking.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0"><i className="ri-check-line text-accent-500"></i></span>
                    <span>Categories are updated on a defined schedule with documented change history.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0"><i className="ri-check-line text-accent-500"></i></span>
                    <span>Content is reviewed for accuracy, relevance, and helpfulness on an ongoing basis.</span>
                  </li>
                </ul>
              </section>

              <section className="mt-8">
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-3">Update Log</h2>
                <div className="space-y-3">
                  <div className="flex items-start gap-4 pb-3 border-b border-background-200/70">
                    <span className="text-xs font-label font-semibold text-foreground-500 whitespace-nowrap pt-0.5">July 2026</span>
                    <div>
                      <p className="text-sm font-body font-semibold text-foreground-800">Comprehensive site launch</p>
                      <p className="text-xs text-foreground-500 mt-0.5">All 14 categories populated with initial product data. Complete SEO guide published.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 pb-3 border-b border-background-200/70">
                    <span className="text-xs font-label font-semibold text-foreground-500 whitespace-nowrap pt-0.5">June 2026</span>
                    <div>
                      <p className="text-sm font-body font-semibold text-foreground-800">Category structure finalized</p>
                      <p className="text-xs text-foreground-500 mt-0.5">14-category architecture defined. Spreadsheet formatting standards established.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="text-xs font-label font-semibold text-foreground-500 whitespace-nowrap pt-0.5">May 2026</span>
                    <div>
                      <p className="text-sm font-body font-semibold text-foreground-800">Project inception</p>
                      <p className="text-xs text-foreground-500 mt-0.5">Research and planning phase. Community feedback gathered to identify highest-demand categories.</p>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-label font-semibold text-foreground-900">Return to the resource hub</p>
                  <p className="text-xs font-body text-foreground-500 mt-0.5">Browse categories or read the complete guide</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/"
                    className="cta-button inline-flex items-center gap-2 px-5 py-2.5 bg-foreground-900 text-white text-sm font-label font-semibold rounded-md hover:bg-foreground-800 transition-colors"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-home-line"></i></span>
                    Back to Home
                  </Link>
                  <Link
                    to="/article/hipobuy-spreadsheet-guide"
                    className="cta-button inline-flex items-center gap-2 px-5 py-2.5 bg-accent-500 text-white text-sm font-label font-semibold rounded-md hover:bg-accent-600 transition-colors"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-book-open-line"></i></span>
                    Read the Guide
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}